import { graphqlQuery } from '@/lib/api/graphql';
import { buildGraphQLSortVariable as buildSort, type GraphQLConnection, pageToCursor } from '@/lib/graphql-pagination';
import { generateMockStrategies } from '@/lib/mock-data/strategies-mock-data';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { CollectionStrategyItem } from '@/types/collection-strategies';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL types ---

interface StrategyGraphQLNode {
  strategyId: number;
  organisationId: number;
  companyGroupId: number;
  name: string | null;
  strategyTypeName: string | null;
  description: string | null;
  status: string | null;
  openCasesCount: number;
  avgDaysOpen: number | null;
  createdAt: string;
  updatedAt: string;
}

interface StrategyListGraphQLResponse {
  collectionStrategies: {
    strategyList: GraphQLConnection<StrategyGraphQLNode>;
  };
}

// --- Params ---

export interface GetStrategiesGraphQLParams {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// --- GraphQL query ---

const STRATEGY_LIST_QUERY = `
  query StrategyList($first: Int, $after: String, $sort: [StrategyListSortSortByInput!], $filter: StrategyListFilterInput) {
    collectionStrategies {
      strategyList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            strategyId
            organisationId
            companyGroupId
            name
            strategyTypeName
            description
            status
            openCasesCount
            avgDaysOpen
            createdAt
            updatedAt
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToStrategy(node: StrategyGraphQLNode): CollectionStrategyItem {
  const strategyType = (node.strategyTypeName || 'Standard') as CollectionStrategyItem['type'];
  return {
    id: String(node.strategyId),
    name: node.name || '',
    type: strategyType,
    description: node.description || '',
    status: node.status === 'Active' ? 'Active' : 'Inactive',
    openCasesCount: node.openCasesCount,
    avgDaysOpen: node.avgDaysOpen ?? 0,
    trigger: { type: 'days_overdue', daysOverdue: 30 },
    actions: [],
    assignmentRules: [],
    performanceMetrics: {
      totalCasesProcessed: 0,
      successfulResolutions: 0,
      averageResolutionTime: 0,
      collectionRate: 0,
    },
    createdAt: new Date(node.createdAt),
    updatedAt: new Date(node.updatedAt),
  };
}

// --- Sort / Filter builders ---

const sortFieldMap: Record<string, string> = {
  name: 'Name',
  type: 'StrategyTypeName',
  status: 'Status',
  openCasesCount: 'OpenCasesCount',
  avgDaysOpen: 'AvgDaysOpen',
  createdAt: 'CreatedAt',
  updatedAt: 'UpdatedAt',
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: 'asc' | 'desc') {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildGraphQLFilterVariable(params: GetStrategiesGraphQLParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.type && params.type !== 'all') {
    filter.strategyTypeName = { eq: params.type };
  }
  if (params.status && params.status !== 'all') {
    filter.status = { eq: params.status };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mock helpers ---

const mockStrategies = generateMockStrategies();

function filterMockStrategies(
  strategies: CollectionStrategyItem[],
  params: GetStrategiesGraphQLParams,
): CollectionStrategyItem[] {
  return strategies.filter((x) => {
    const matchesSearch =
      !params.search ||
      x.name.toLowerCase().includes(params.search.toLowerCase()) ||
      x.description.toLowerCase().includes(params.search.toLowerCase());

    const matchesType = !params.type || params.type === 'all' || x.type === params.type;
    const matchesStatus = !params.status || params.status === 'all' || x.status === params.status;

    return matchesSearch && matchesType && matchesStatus;
  });
}

// --- Hook ---

export function useStrategiesListGraphQL(params?: GetStrategiesGraphQLParams) {
  const apiConfig = useApiConfig();
  const queryParams: GetStrategiesGraphQLParams = { page: 1, pageSize: 50, ...params };
  const filteredMock = filterMockStrategies(mockStrategies, queryParams);

  return useModeAwareQuery<CollectionStrategyItem[], Error>(
    ['strategies-graphql-list', queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 50;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<StrategyListGraphQLResponse>(
        apiConfig,
        STRATEGY_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.collectionStrategies.strategyList;
      return connection.edges.map((x) => mapGraphQLNodeToStrategy(x.node));
    },
    filteredMock,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
